import _ from "lodash";

import { IUpperDataStore } from "src/stores/upperData";
import { IMainStore } from "src/stores/main";
import { ISelection } from "../../constants/interfaces/bet";
import { IClientConfig } from "src/client-config/config";
import { NotifyTypeEnum } from "src/constants/enums/notifyTypes";
import { hasInactiveSelections, stakeOutOfLimits, hasNotLoadedSelections } from "src/rules/bet";
import { getBetMaxStake, getBetMinStake } from "src/common/helpers/restrictions";
import {
	expressIdIncompatibility,
	incompleteBetBuilderCard,
	invalidBalance,
	invalidMinOdd,
	possibleWinOutOfLimits,
	betBuilderOddsLoading,
	betBuilderMissingTotalOdd,
	IBetBuilderValidationState,
} from "src/rules/bet/validations";
import { isBookingOnlyMode } from "src/rules/booking";

const notifyMessages = [
	{
		id: _.uniqueId('notify_message'),
		type: NotifyTypeEnum.Info,
		message: (
			config: IClientConfig,
			selections: ISelection[],
			mainState: IMainStore,
			upperDataState: IUpperDataStore
		) => {
			const { bonusRules } = upperDataState
			const rules = [...bonusRules]

			if (!rules.length || !config.showBonusRulesHint) return null;

			const bonusRule = rules.sort((a, b) => a.MinOdds - b.MinOdds)[0]

			if (selections.some(selection => selection.event.price < bonusRule?.MinOdds && (selections.length >= bonusRule?.MinimumSelections)))
				return 'At least one of the selection odds is below the minimum required to qualify for the bonus';

			return null;
		}
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (
			config: IClientConfig,
			selections: ISelection[],
			mainState: IMainStore,
			upperDataState: IUpperDataStore
		) => {
			if (!invalidBalance(config, selections, mainState, upperDataState) || isBookingOnlyMode(config, upperDataState)) return null;
			return "Insufficient balance";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (config: IClientConfig, selections: ISelection[]) => {
			if (!hasInactiveSelections(config, selections)) return null;
			return "There are deleted events";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (config: IClientConfig, selections: ISelection[]) => {
			if (!incompleteBetBuilderCard(config, selections)) return null;
			return "Bet builder card needs at least two selections";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (config: IClientConfig, selections: ISelection[]) => {
			if (!expressIdIncompatibility(config, selections)) return null;
			return "Some same-game selections cannot be combined with the bet builder card";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Info,
		message: (
			_config: IClientConfig,
			selections: ISelection[],
			_mainState: IMainStore,
			_upperDataState: IUpperDataStore,
			bbState?: IBetBuilderValidationState,
		) => {
			if (!betBuilderOddsLoading(selections, bbState)) return null;
			return "Calculating bet builder odd, please wait";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (
			_config: IClientConfig,
			selections: ISelection[],
			_mainState: IMainStore,
			_upperDataState: IUpperDataStore,
			bbState?: IBetBuilderValidationState,
		) => {
			if (!betBuilderMissingTotalOdd(selections, bbState)) return null;
			return "Bet builder card has no available odd, cannot place bet";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		debounce: true,
		type: NotifyTypeEnum.Warning,
		message: (config: IClientConfig, selections: ISelection[]) => {
			if (!hasNotLoadedSelections(config, selections)) return null;
			return "Some events in betslip are not available at the moment";
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Info,
		message: (
			config: IClientConfig,
			selections: ISelection[],
			mainState: IMainStore,
			upperDataState: IUpperDataStore
		) => {
			if (!stakeOutOfLimits(config, selections, mainState, upperDataState)) return null;

			const minStake = getBetMinStake(config, selections, mainState, upperDataState);
			const maxStake = getBetMaxStake(config, mainState);

			if (_.isNil(minStake) || _.isNil(maxStake)) return null;

			return ["Min Bet Amount {1} - Max Bet Amount {2}", `${minStake}`, `${maxStake}`];
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Info,
		message: (config: IClientConfig, selections: ISelection[], mainState: IMainStore) => {
			if (!config.restrictions?.minimalOdd || !invalidMinOdd(config, selections, mainState))
				return null;

			return [
				"Minimum odd should be {1} . Change events to place a bet",
				config.restrictions.minimalOdd.toString(),
			];
		},
	},
	{
		id: _.uniqueId("notify_message"),
		type: NotifyTypeEnum.Warning,
		message: (
			config: IClientConfig,
			selections: ISelection[],
			mainState: IMainStore,
			upperDataState: IUpperDataStore
		) => {
			if (!possibleWinOutOfLimits(config, selections, mainState, upperDataState)) return null;

			let errorMessage = "Max possible win amount exceeds";

			if (!config.superbetHiddenMode && !config.enableAutomaticSuperbet && config.enableSuperbetToggle) errorMessage += ". Please activate superbet mode";

			return errorMessage;
		},
	},
];

export const getNotifyMessages = (config: IClientConfig) => {
	if (!config.notifyMessages?.length) return notifyMessages;

	return notifyMessages.concat(
		...config.notifyMessages.map(item => ({
			id: _.uniqueId("notify_message"),
			type: item.type,
			message: () => item.message,
		}))
	);
};
